import { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, Clock } from "lucide-react";
import { isPromoService, isLegacyHidden, sortPromos, groupByMenu, splitName, cleanName, menuCategory, loadTreatmentCodes, codeOf, matchesCode } from "../../lib/serviceMenu";

// Last Minute Club picker: search → promotions → collapsed categories (one open at a time).
const Row = ({ svc, selected, onPick, code, isFr }) => {
  const { title, detail } = splitName(svc.name);
  return (
    <button type="button" onClick={() => onPick(svc)}
      className={`w-full text-left px-3 py-2.5 rounded-lg border transition ${selected ? "border-amber-400 bg-amber-500/15 text-amber-100" : "border-zinc-800 bg-zinc-900/40 text-zinc-300 hover:bg-zinc-900/70 hover:border-zinc-700"}`}
      data-testid={`lmb-service-${svc.variation_id}`}>
      <div className="flex items-start gap-2">
        {code && <span className="shrink-0 mt-0.5 px-1.5 py-0.5 rounded bg-amber-300 text-zinc-900 text-[12px] font-black tracking-wider ring-1 ring-amber-500/40">#{code}</span>}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium leading-snug">{title}</div>
          {detail && <div className="text-[11px] text-zinc-500 leading-snug line-clamp-1">{detail}</div>}
        </div>
        <div className="shrink-0 text-right">
          {svc.price && <div className="text-xs font-bold text-amber-300">{String(svc.price).startsWith("$") ? svc.price : `$${svc.price}`}</div>}
          <div className="text-[10px] text-zinc-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{svc.duration_minutes} {isFr ? "min" : "min"}</div>
        </div>
      </div>
    </button>
  );
};

export default function LmbServicePicker({ services, selected, onPick, isFr }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [codes, setCodes] = useState(null);
  useEffect(() => { loadTreatmentCodes().then(setCodes); }, []);

  const { promos, groups, hits } = useMemo(() => {
    const t = q.trim().toLowerCase();
    const pool = services.filter((s) => !isLegacyHidden(s) && !/do not book/i.test(s.name || ""));
    if (t) {
      const byCode = pool.filter((s) => matchesCode(t, s, codes));
      const hits = byCode.length ? byCode : pool.filter((s) => `${s.name} ${menuCategory(s)}`.toLowerCase().includes(t));
      return { promos: [], groups: [], hits };
    }
    return { promos: sortPromos(pool.filter(isPromoService)), groups: groupByMenu(pool.filter((s) => !isPromoService(s))), hits: null };
  }, [services, q, codes]);

  useEffect(() => { if (open === null && promos.length === 0 && groups.length) setOpen(groups[0][0]); }, [promos.length, groups, open]);

  return (
    <div className="space-y-2" data-testid="lmb-picker">
      <div className="relative">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isFr ? "Botox, lèvres, facial, laser ou un numéro (#12)…" : "Search Botox, lips, facial, laser or a number (#12)…"}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-400" data-testid="lmb-search" />
      </div>
      <div className="max-h-[46vh] overflow-y-auto pr-1 space-y-2">
        {hits ? (
          hits.length ? hits.map((s) => <Row key={s.variation_id} svc={s} selected={selected?.variation_id === s.variation_id} onPick={onPick} code={codeOf(s, codes)} isFr={isFr} />)
            : <p className="text-zinc-500 text-xs text-center py-4" data-testid="lmb-no-results">{isFr ? "Aucun soin trouvé — essayez un autre mot ou le numéro du soin." : "Nothing found — try another word or the treatment number."}</p>
        ) : (
          <>
            {promos.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-300 px-1">{isFr ? "Promotions & forfaits" : "Promotions & packages"} <span className="text-zinc-500">({promos.length})</span></p>
                {promos.map((s) => <Row key={s.variation_id} svc={s} selected={selected?.variation_id === s.variation_id} onPick={onPick} code={codeOf(s, codes)} isFr={isFr} />)}
              </div>
            )}
            {groups.map(([cat, list]) => (
              <div key={cat} className="rounded-xl border border-zinc-800 overflow-hidden">
                <button type="button" onClick={() => setOpen(open === cat ? null : cat)} className="w-full flex items-center justify-between px-3 py-2.5 bg-zinc-900/60 hover:bg-zinc-900 text-left" data-testid={`lmb-cat-${cat.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                  <span className="text-sm font-semibold text-white">{cat} <span className="text-zinc-500 text-xs font-normal">({list.length})</span></span>
                  <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open === cat ? "rotate-180" : ""}`} />
                </button>
                {open === cat && <div className="p-2 space-y-1.5 bg-zinc-950/40">{list.map((s) => <Row key={s.variation_id} svc={s} selected={selected?.variation_id === s.variation_id} onPick={onPick} code={codeOf(s, codes)} isFr={isFr} />)}</div>}
              </div>
            ))}
          </>
        )}
      </div>
      {selected && <p className="text-xs text-emerald-300 px-1" data-testid="lmb-selected-label">{isFr ? "Choisi :" : "Selected:"} <b>{cleanName(selected.name)}</b></p>}
    </div>
  );
}
